import { Form, Formik } from 'formik';
import React from 'react';
import { getQuickVideoInitialValues } from '../helpers/getQuickVideoInitialValues';
import { quickVideoValidationSchema } from '../helpers/quickVideoValidation';
import {
  IQuickVideoForm,
  IUpsertQuickVideoBody,
  IUpsertQuickVideoPart,
} from 'lib/api/quickVideo/types';
import { FullScreenModal } from 'lib/components/FullScreenModal';
import { Button } from 'react-covideo-common';
import { ParagraphNormalBold } from 'lib/components/styles/typography';
import { Gap } from 'lib/components/styles/layout';
import { useGetConfiguration } from 'lib/api/quickVideo/useGetConfiguration';
import { QuickVideoDetailsForm } from '../components/forms/QuickVideoDetailsForm';
import { LoadingIndicator } from 'lib/components';
import { useAuth } from 'lib/context';
import { useCreateQuickVideoMutation } from 'lib/api/quickVideo/useCreateQuickVideoMutation';
import { groupBy, omit } from 'lodash';
import { useEditQuickVideoMutation } from 'lib/api/quickVideo/useEditQuickVideoMutation';
import { useHistory, useParams } from 'react-router';
import { useGetQuickVideo } from 'lib/api/quickVideo/useGetQuickVideo';
import { FaArrowLeft } from 'react-icons/fa';
import { useTheme } from 'styled-components/macro';
import { QuickVideoCustomizeEmailSmsForm } from '../components/forms/QuickVideoCustomizeEmailSmsForm';

export const UpsertQuickVideoModal = () => {
  const history = useHistory();
  const theme = useTheme();
  const { quickVideoId } = useParams<{ quickVideoId: string }>();
  const isEditMode = !!quickVideoId;
  const { userData } = useAuth();

  const { data: quickVideo, isLoading: isQuickVideoLoading } = useGetQuickVideo(
    {
      quickVideoId: Number(quickVideoId) || 0,
      enabled: !!quickVideoId,
    }
  );

  const {
    mutateAsync: createQuickVideoMutation,
    isLoading: isCreatingQuickVideo,
  } = useCreateQuickVideoMutation({});

  const {
    mutateAsync: editQuickVideoMutation,
    isLoading: isEditingQuickVideo,
  } = useEditQuickVideoMutation({
    quickVideoId: quickVideo?.quickVideoId || 0,
  });

  const { data: configurationData, isLoading: isConfigurationLoading } =
    useGetConfiguration();

  const categoryOptions = configurationData?.categories || [];
  const videoPositionDropdowns = configurationData?.positions || [];
  const allVideoPartOptions = groupBy(configurationData?.parts || [], 'type');

  const onSubmit = async (values: IQuickVideoForm) => {
    const quickVideoParts = values.quickVideoParts.map(part => {
      if (!!part.video) {
        return {
          partType: part.partType,
          partPosition: part.partPosition,
          videoId: part.video.id,
          description: part.description || '',
          quickVideoPartId: part?.quickVideoPartId,
        };
      }
      return { ...part, quickVideoPartId: part?.quickVideoPartId };
    }) as IUpsertQuickVideoPart[];

    const requestBody: IUpsertQuickVideoBody = {
      ...omit(values, ['quickVideoParts']),
      quickVideoParts,
      userId: Number(userData.id),
      customerId: Number(userData.customerId),
    };

    if (isEditMode) {
      await editQuickVideoMutation({
        ...requestBody,
        quickVideoId: Number(quickVideoId),
      });
    } else {
      await createQuickVideoMutation({
        ...requestBody,
        quickVideoParts: quickVideoParts
          .filter(part => !!part.partType)
          .map(part => omit(part, ['quickVideoPartId'])),
      });
    }

    // navigate to quick video list
    history.push('/ai-configuration/quick-videos');
  };

  const title = isEditMode ? 'Edit Quick Video' : 'Add A New Quick Video';
  const submitButtonText = isEditMode ? 'Save Changes' : 'Add Quick Video';
  const isLoading = isConfigurationLoading || isQuickVideoLoading;

  if (isLoading) {
    return (
      <FullScreenModal>
        <LoadingIndicator height={'100%'} isLoading />
      </FullScreenModal>
    );
  }
  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={getQuickVideoInitialValues({
        defaultCategory: categoryOptions[0].value,
        videoPositionDropdowns,
        quickVideo,
      })}
      validationSchema={quickVideoValidationSchema({
        requiredPositions: videoPositionDropdowns.filter(
          position => position.required
        ),
      })}
      validateOnMount
      validateOnChange
    >
      {({ isValid, handleSubmit }) => {
        return (
          <Form onSubmit={handleSubmit}>
            <FullScreenModal
              headerLeft={
                <Button
                  variant='transparent'
                  icon={<FaArrowLeft color={theme.colors.neutral[60]} />}
                  onClick={() => history.push('/ai-configuration/quick-videos')}
                />
              }
              headerCenter={<ParagraphNormalBold>{title}</ParagraphNormalBold>}
              headerRight={
                <Button
                  text={submitButtonText}
                  disabled={
                    !isValid || isCreatingQuickVideo || isEditingQuickVideo
                  }
                  type='submit'
                />
              }
            >
              <Gap
                justifyContent='center'
                style={{
                  padding: '48px 0',
                }}
              >
                <Gap
                  flexDirection='column'
                  alignItems='center'
                  width='891px'
                  gap='40px'
                >
                  <QuickVideoDetailsForm
                    categoryOptions={categoryOptions}
                    videoPositionDropdowns={videoPositionDropdowns}
                    allVideoPartOptions={allVideoPartOptions}
                  />
                  <QuickVideoCustomizeEmailSmsForm />
                </Gap>
              </Gap>
            </FullScreenModal>
          </Form>
        );
      }}
    </Formik>
  );
};
